import React, { useState, useEffect } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import "./AudienceProfile.scss";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const AudienceProfile = () => {
  const [smePercentage, setSmePercentage] = useState(0);
  const [largePercentage, setLargePercentage] = useState(0);
  const [investorPercentage, setInvestorPercentage] = useState(0);
  const [totalPercentage, setTotalPercentage] = useState(0);

  useEffect(() => {
    // Animate percentages
    const duration = 2000; // 2 seconds
    const steps = 60;
    const interval = duration / steps;

    // SMEs: 0 to 70
    const smeStep = 70 / steps;
    let smeCount = 0;
    const smeInterval = setInterval(() => {
      smeCount += smeStep;
      if (smeCount >= 70) {
        setSmePercentage(70);
        clearInterval(smeInterval);
      } else {
        setSmePercentage(Math.floor(smeCount));
      }
    }, interval);

    // Large: 0 to 20
    const largeStep = 20 / steps;
    let largeCount = 0;
    const largeInterval = setInterval(() => {
      largeCount += largeStep;
      if (largeCount >= 20) {
        setLargePercentage(20);
        clearInterval(largeInterval);
      } else {
        setLargePercentage(Math.floor(largeCount));
      }
    }, interval);

    // Investors: 0 to 10
    const investorStep = 10 / steps;
    let investorCount = 0;
    const investorInterval = setInterval(() => {
      investorCount += investorStep;
      if (investorCount >= 10) {
        setInvestorPercentage(10);
        clearInterval(investorInterval);
      } else {
        setInvestorPercentage(Math.floor(investorCount));
      }
    }, interval);

    // Total: 0 to 100
    const totalStep = 100 / steps;
    let totalCount = 0;
    const totalInterval = setInterval(() => {
      totalCount += totalStep;
      if (totalCount >= 100) {
        setTotalPercentage(100);
        clearInterval(totalInterval);
      } else {
        setTotalPercentage(Math.floor(totalCount));
      }
    }, interval);

    return () => {
      clearInterval(smeInterval);
      clearInterval(largeInterval);
      clearInterval(investorInterval);
      clearInterval(totalInterval);
    };
  }, []);

  // Chart.js data configuration
  const chartData = {
    labels: ["SMEs", "Large Enterprises", "Investors & Intl Delegates"],
    datasets: [
      {
        data: [smePercentage, largePercentage, investorPercentage],
        backgroundColor: ["#4A90E2", "#7ED321", "#F5A623"],
        borderWidth: 0,
        cutout: "70%",
      },
    ],
  };

  // Chart.js options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: {
          size: 14,
          weight: "bold",
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: function (context) {
            return context.label + ": " + context.parsed + "%";
          },
        },
      },
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 2000,
    },
  };

  return (
    <section className="audience-profile-section section-padding">
      <div className="container">
        <div className="row">
          <div className="col-lg-12">
            <div className="section-title text-center">
              <h2>Audience Profile</h2>
              <p>
                Connect with a diverse audience of business leaders,
                entrepreneurs, and investors from across the globe.
              </p>
            </div>
          </div>
        </div>
        <div className="row align-items-center">
          <div className="col-lg-6 col-md-6">
            <div className="audience-chart">
              <div className="donut-chart">
                <div className="chart-container">
                  <div style={{ position: "relative", maxWidth: "350px", margin: "0 auto" }}>
                    <Doughnut data={chartData} options={chartOptions} />
                    <div className="chart-center">
                      <span className="chart-percentage">{totalPercentage}%</span>
                      <span className="chart-label">Total Audience</span>
                    </div>
                  </div>
                </div>
                <div className="chart-legend">
                  <div className="legend-item">
                    <div
                      className="legend-color"
                      style={{ backgroundColor: "#4A90E2" }}
                    ></div>
                    <span>SMEs ({smePercentage}%)</span>
                  </div>
                  <div className="legend-item">
                    <div
                      className="legend-color"
                      style={{ backgroundColor: "#7ED321" }}
                    ></div>
                    <span>Large Enterprises ({largePercentage}%)</span>
                  </div>
                  <div className="legend-item">
                    <div
                      className="legend-color"
                      style={{ backgroundColor: "#F5A623" }}
                    ></div>
                    <span>
                      Investors & Intl Delegates ({investorPercentage}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-6 col-md-6">
            <div className="audience-details">
              <h3>Who Attends?</h3>
              <div className="audience-list">
                <div className="audience-item">
                  <div className="audience-icon">
                    <div
                      className="icon-circle"
                      style={{ backgroundColor: "#4A90E2" }}
                    >
                      <i className="fa fa-building"></i>
                    </div>
                  </div>
                  <div className="audience-content">
                    <h4>Small & Medium Enterprises ({smePercentage}%)</h4>
                    <p>
                      Business owners and executives looking to optimize cash
                      flow and scale operations.
                    </p>
                  </div>
                </div>
                <div className="audience-item">
                  <div className="audience-icon">
                    <div
                      className="icon-circle"
                      style={{ backgroundColor: "#7ED321" }}
                    >
                      <i className="fa fa-building-o"></i>
                    </div>
                  </div>
                  <div className="audience-content">
                    <h4>Large Enterprises ({largePercentage}%)</h4>
                    <p>
                      Corporate leaders seeking innovative strategies to enhance
                      financial operations and efficiency.
                    </p>
                  </div>
                </div>
                <div className="audience-item">
                  <div className="audience-icon">
                    <div
                      className="icon-circle"
                      style={{ backgroundColor: "#F5A623" }}
                    >
                      <i className="fa fa-star"></i>
                    </div>
                  </div>
                  <div className="audience-content">
                    <h4>
                      Investors & International Delegates ({investorPercentage}
                      %)
                    </h4>
                    <p>
                      Angel investors, venture capitalists, and global business
                      leaders looking for partnership opportunities.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Industry Representation Section */}
        <div className="row mt-5">
          <div className="col-lg-12">
            <div className="section-title text-center">
              <h2>Industry Representation</h2>
            </div>
          </div>
        </div>
        <div className="row mt-4">
          <div className="col-lg-2 col-md-4 col-sm-6 col-6">
            <div className="industry-card">
              <div className="industry-icon">
                <i className="ti-desktop"></i>
              </div>
              <h4>Technology</h4>
              <p>22%</p>
            </div>
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 col-6">
            <div className="industry-card">
              <div className="industry-icon">
                <i className="ti-briefcase"></i>
              </div>
              <h4>Finance</h4>
              <p>18%</p>
            </div>
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 col-6">
            <div className="industry-card">
              <div className="industry-icon">
                <i className="ti-shopping-cart-full"></i>
              </div>
              <h4>Retail</h4>
              <p>15%</p>
            </div>
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 col-6">
            <div className="industry-card">
              <div className="industry-icon">
                <i className="ti-heart"></i>
              </div>
              <h4>Healthcare</h4>
              <p>12%</p>
            </div>
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 col-6">
            <div className="industry-card">
              <div className="industry-icon">
                <i className="ti-layout-grid3-alt"></i>
              </div>
              <h4>Real Estate</h4>
              <p>10%</p>
            </div>
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 col-6">
            <div className="industry-card">
              <div className="industry-icon">
                <i className="ti-plus"></i>
              </div>
              <h4>Others</h4>
              <p>23%</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AudienceProfile;
